import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, TreePine } from 'lucide-react';
import { fechaCorta, plural } from 'lib/auditoria';
import { rutaArbol } from 'lib/arboles';
import { RiesgoDiscreto } from 'components/RiesgoPill';
import Bloque, { Cifras } from './Bloque';

const nombre = (a) => a.nombre_comun || a.especie || '';

function Proxima({ a }) {
  if (a.vencida) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap text-muted-foreground">
        <CalendarClock size={12} aria-hidden="true" />Vencida{a.proxima_evaluacion ? ` (${fechaCorta(a.proxima_evaluacion)})` : ''}
      </span>
    );
  }
  return a.proxima_evaluacion
    ? <span className="whitespace-nowrap">{fechaCorta(a.proxima_evaluacion)}</span>
    : <span className="text-muted-foreground">Sin fecha</span>;
}

const Ultima = ({ a }) => (a.ultima_fecha ? (
  <>
    <span className="whitespace-nowrap">{fechaCorta(a.ultima_fecha)}</span>
    {a.ultimo_evaluador && <span className="block truncate text-xs text-muted-foreground">{a.ultimo_evaluador}</span>}
  </>
) : <span className="text-muted-foreground">Sin evaluación</span>);

export function textoArboles(r) {
  if (!r.arboles) return 'No hay árboles registrados en esta instalación.';
  return `${plural(r.evaluadosPeriodo, 'árbol evaluado', 'árboles evaluados')} en el período, de ${r.arboles}.`;
}

export default function BloqueArboles({ control, error }) {
  const r = control?.resumen;
  return (
    <Bloque titulo="Árboles" icono={TreePine} testId="bloque-arboles"
      resumen={control ? textoArboles(r) : 'No se pudo consultar los árboles.'}
      cifras={control && (
        <Cifras items={[
          { label: 'Árboles', valor: r.arboles },
          { label: 'Evaluados en el período', valor: r.evaluadosPeriodo },
          { label: 'Evaluaciones en el período', valor: r.evaluacionesPeriodo },
          { label: 'Evaluación vencida', valor: r.vencidas },
          { label: 'Riesgo medio o mayor', valor: r.riesgoMedioOMas, tono: r.riesgoAlto ? 'falla' : '' },
        ]} />
      )}>
      {!control ? (
        <p className="text-sm text-muted-foreground">{error || 'No se pudo consultar los árboles.'} El reporte lo indicará.</p>
      ) : control.filas.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay árboles registrados en esta instalación.</p>
      ) : (
        <>
          {!control.evaluacionesDisponibles && (
            <p className="mb-2 text-xs text-muted-foreground">No se pudo consultar las evaluaciones: las del período no se cuentan.</p>
          )}
          <table className="hidden w-full text-sm sm:table" data-testid="tabla-arboles">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-1.5 pr-3 font-medium">Árbol</th>
                <th className="py-1.5 pr-3 font-medium">Plataforma</th>
                <th className="py-1.5 pr-3 font-medium">Riesgo</th>
                <th className="py-1.5 pr-3 font-medium">Última evaluación</th>
                <th className="py-1.5 pr-3 text-right font-medium">En el período</th>
                <th className="py-1.5 font-medium">Próxima</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {control.filas.map((a) => (
                <tr key={a.id} className="align-top">
                  <td className="py-2 pr-3">
                    <Link to={rutaArbol(a.id)} className="font-semibold text-primary underline-offset-2 hover:underline">{a.codigo}</Link>
                    {nombre(a) && <span className="block text-xs text-muted-foreground">{nombre(a)}</span>}
                  </td>
                  <td className="py-2 pr-3 text-foreground">{a.sostiene || <span className="text-muted-foreground">Sin dato</span>}</td>
                  <td className="py-2 pr-3"><RiesgoDiscreto riesgo={a.riesgo} /></td>
                  <td className="max-w-[12rem] py-2 pr-3 text-foreground"><Ultima a={a} /></td>
                  <td className="py-2 pr-3 text-right tabular-nums text-foreground">{a.nPeriodo}</td>
                  <td className="py-2 text-foreground"><Proxima a={a} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="divide-y divide-border sm:hidden" aria-label="Árboles">
            {control.filas.map((a) => (
              <li key={a.id}>
                <Link to={rutaArbol(a.id)} className="block py-2.5">
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-primary">{a.codigo}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{[nombre(a), a.sostiene].filter(Boolean).join(', ')}</span>
                    <RiesgoDiscreto riesgo={a.riesgo} />
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {a.ultima_fecha ? `Última: ${fechaCorta(a.ultima_fecha)}` : 'Sin evaluación'}
                    {`, ${plural(a.nPeriodo, 'evaluación', 'evaluaciones')} en el período`}
                    {a.vencida ? ', vencida' : a.proxima_evaluacion ? `, próxima ${fechaCorta(a.proxima_evaluacion)}` : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </Bloque>
  );
}
